import { Link } from "react-router-dom";
import { ArrowRight, MapPin, HeartHandshake, Sprout } from "lucide-react";
import CountUp from "../components/CountUp";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO — angle identité / structure */}
      <section className="bg-ink text-white relative overflow-hidden nx-hero-apropos" style={{ paddingTop: 200, paddingBottom: 120 }}>
        <div className="absolute inset-0 nx-hero-grid opacity-50"></div>
        <div
          className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 70%)" }}
        ></div>
        <div className="nx-container relative">
          <div className="max-w-3xl nx-fade-up">
            <span className="nx-overline">Qui sommes-nous</span>
            <h1 className="text-white" style={{ marginTop: 24, marginBottom: 32 }}>
              Une structure agile, <span style={{ color: "#00C8FF" }}>pensée pour le facility management d'aujourd'hui.</span>
            </h1>
            <p className="text-mist" style={{ fontSize: 20, marginBottom: 20 }}>
              NOVANEXT est née d'un constat de terrain&nbsp;: les gestionnaires de sites perdent un temps précieux à jongler entre des prestataires cloisonnés. Nous avons construit une organisation différente — plus intégrée, plus proche, plus lisible.
            </p>
            <p className="text-mist" style={{ fontSize: 20, marginBottom: 0 }}>
              Notre métier consiste à décharger les entreprises, collectivités et gestionnaires de sites de tout ce qui n'est pas leur cœur d'activité, en garantissant une exécution rigoureuse au quotidien.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION / VISION — reformulé sans doublon "interlocuteur" */}
      <section className="bg-white nx-arch-grid-light" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <div className="nx-container">
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 30 }}>
            <div className="nx-card nx-fade-up">
              <div className="nx-icon-wrap"><Sprout size={26} strokeWidth={1.75} /></div>
              <span className="nx-overline mt-6 block" style={{ marginTop: 24 }}>Notre raison d'être</span>
              <h3 style={{ marginTop: 8, marginBottom: 24 }}>Simplifier la vie des gestionnaires de sites.</h3>
              <p style={{ marginBottom: 0 }}>
                Nous croyons qu'un site bien tenu ne devrait jamais être une source d'inquiétude ni de perte de temps. Nous prenons en charge la coordination opérationnelle complète — pour vous rendre du temps, de la sérénité et de la lisibilité.
              </p>
            </div>
            <div className="nx-card nx-fade-up nx-delay-2">
              <div className="nx-icon-wrap"><HeartHandshake size={26} strokeWidth={1.75} /></div>
              <span className="nx-overline mt-6 block" style={{ marginTop: 24 }}>Notre manière de travailler</span>
              <h3 style={{ marginTop: 8, marginBottom: 24 }}>Une relation de partenaire, pas de prestataire.</h3>
              <p style={{ marginBottom: 0 }}>
                La fidélité de nos clients passe par la constance et la transparence. Nous privilégions les échanges directs, les engagements écrits et l'amélioration continue plutôt que la promesse commerciale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CHIFFRES CLES — 24/7 remplacé par 7j/7 (astreinte technique, cf. distinction délais) */}
      <section className="bg-navy text-white nx-enbref-bg" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <div className="nx-container">
          <div className="max-w-2xl">
            <span className="nx-overline">En bref</span>
            <h2 className="text-white" style={{ marginTop: 24, marginBottom: 32 }}>
              Une structure agile, une ambition d'exigence.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 30, marginTop: 64 }}>
            {[
              { k: "5", v: "pôles d'expertise" },
              { k: "7j/7", v: "astreinte technique" },
              { k: "100%", v: "demandes étudiées" },
            ].map((c, i) => (
              <div key={c.v} className={`nx-card-dark nx-fade-up nx-delay-${i + 1}`}>
                <div style={{ fontSize: 42, fontWeight: 700, color: "#00C8FF", lineHeight: 1 }}>
                  <CountUp value={c.k} duration={1500} />
                </div>
                <div className="text-mist" style={{ marginTop: 16, fontSize: 15 }}>{c.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NOTRE ANCRAGE — remplace "Quatre exigences non négociables" (déplacé sur /pourquoi-nous)
          Contenu d'identité : histoire, valeurs humaines, zone d'intervention réelle. */}
      <section className="bg-white nx-arch-grid-light" style={{ paddingTop: 120, paddingBottom: 120 }} data-testid="about-identity">
        <div className="nx-container">
          <div className="max-w-2xl">
            <span className="nx-overline">Notre ancrage</span>
            <h2 style={{ marginTop: 24, marginBottom: 32 }}>Nos racines, nos équipes, nos engagements.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 30, marginTop: 64 }}>
            <div className="nx-card nx-fade-up nx-delay-1" data-testid="identity-history">
              <div className="nx-icon-wrap"><Sprout size={26} strokeWidth={1.75} /></div>
              <h4 style={{ marginTop: 24, marginBottom: 16 }}>Notre histoire</h4>
              <p style={{ fontSize: 16, marginBottom: 0 }}>
                Créée en 2024 à Stains, en Seine-Saint-Denis, NOVANEXT est née d'une conviction&nbsp;: les entreprises méritent un partenaire réactif, humain et professionnel — capable de couvrir l'ensemble de leurs services généraux sans multiplier les intermédiaires.
              </p>
            </div>
            <div className="nx-card nx-fade-up nx-delay-2" data-testid="identity-values">
              <div className="nx-icon-wrap"><HeartHandshake size={26} strokeWidth={1.75} /></div>
              <h4 style={{ marginTop: 24, marginBottom: 16 }}>Nos valeurs humaines</h4>
              <p style={{ fontSize: 16, marginBottom: 0 }}>
                Chaque intervention est portée par des équipes formées, respectueuses de vos locaux et de vos collaborateurs. Nous croyons qu'une prestation de qualité passe d'abord par la qualité de la relation — avec vos équipes comme avec les nôtres.
              </p>
            </div>
            <div className="nx-card nx-fade-up nx-delay-3" data-testid="identity-territory">
              <div className="nx-icon-wrap"><MapPin size={26} strokeWidth={1.75} /></div>
              <h4 style={{ marginTop: 24, marginBottom: 16 }}>Notre approche</h4>
              <p style={{ fontSize: 16, marginBottom: 0 }}>
                Chaque demande est étudiée selon la nature, la localisation et les exigences de la mission. Cette approche sur mesure garantit une réactivité réelle et un suivi de terrain régulier — jamais délégués à des sous-traitants inconnus.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA — orienté engagement long terme (cohérent avec la page identité) */}
      <section className="bg-ink text-white nx-cta-apropos-bg" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <div className="nx-container text-center">
          <span className="nx-overline">Travaillons ensemble</span>
          <h2 className="text-white" style={{ marginTop: 24, marginBottom: 32, maxWidth: 800, marginLeft: "auto", marginRight: "auto" }}>
            Envie de construire une relation durable avec un partenaire de confiance&nbsp;? Rencontrons-nous.
          </h2>
          <Link to="/contact" className="nx-btn nx-btn-primary" data-testid="about-cta">
            Prendre rendez-vous <ArrowRight size={18} strokeWidth={2} />
          </Link>
        </div>
      </section>
    </div>
  );
}
